import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
  type ComputedRef,
  type ShallowRef,
} from 'vue';

import { resolveVisibleChipCount } from '@/helpers/ChipOverflow/chipOverflow';
import { isServer } from '@/helpers/Platform/platform';

const MAX_CHIP_LINES = 2;
const MEASURE_CHIP_SELECTOR = '[data-test^="service-chip-measure-"]';

const createDebouncedResizeObserver = (
  getElement: () => HTMLElement | null,
  onResize: () => void,
  isBlocked: () => boolean,
) => {
  let resizeObserver: ResizeObserver | null = null;
  let resizeFrameId: number | null = null;

  const disconnect = () => {
    if (resizeFrameId !== null) {
      cancelAnimationFrame(resizeFrameId);
      resizeFrameId = null;
    }
    resizeObserver?.disconnect();
    resizeObserver = null;
  };

  const connect = () => {
    disconnect();

    const element = getElement();
    if (isServer() || !element) {
      return;
    }

    resizeObserver = new ResizeObserver(() => {
      if (isBlocked() || resizeFrameId !== null) {
        return;
      }

      resizeFrameId = requestAnimationFrame(() => {
        resizeFrameId = null;
        onResize();
      });
    });
    resizeObserver.observe(element);
  };

  return { connect, disconnect };
};

export interface AgentCardChipOverflowRefs {
  servicesContainerRef: Readonly<ShallowRef<HTMLElement | null>>;
  measureLayerRef: Readonly<ShallowRef<HTMLElement | null>>;
  overflowMeasureElement: Readonly<ShallowRef<HTMLElement | null>>;
}

export const useAgentCardChipOverflow = (
  chipCount: ComputedRef<number>,
  hiddenChipLabelKeys: ComputedRef<string[]>,
  translateLabel: (labelKey: string) => string,
  { servicesContainerRef, measureLayerRef, overflowMeasureElement }: AgentCardChipOverflowRefs,
) => {
  const visibleChipCount = ref(chipCount.value);
  const hiddenChipCount = ref(0);
  const measureOverflowCount = ref(0);
  let isMeasuring = false;

  const hiddenServicesTitle = computed(() =>
    hiddenChipLabelKeys.value
      .slice(visibleChipCount.value)
      .map((labelKey) => translateLabel(labelKey))
      .join(', '),
  );

  const getMeasureChipElements = (): HTMLElement[] => {
    if (!measureLayerRef.value) {
      return [];
    }

    return Array.from(measureLayerRef.value.querySelectorAll<HTMLElement>(MEASURE_CHIP_SELECTOR));
  };

  const applyOverflowResult = (visibleCount: number, hiddenCount: number) => {
    visibleChipCount.value = visibleCount;
    hiddenChipCount.value = hiddenCount;
  };

  const showAllChips = () => {
    applyOverflowResult(chipCount.value, 0);
  };

  const measureOverflow = async () => {
    measureOverflowCount.value = 0;
    await nextTick();

    const chips = getMeasureChipElements();
    if (chips.length === 0) {
      showAllChips();
      return;
    }

    const firstPass = resolveVisibleChipCount(chips, null, MAX_CHIP_LINES);
    if (firstPass.hiddenCount === 0) {
      showAllChips();
      return;
    }

    measureOverflowCount.value = firstPass.hiddenCount;
    await nextTick();

    const secondPass = resolveVisibleChipCount(chips, overflowMeasureElement.value, MAX_CHIP_LINES);
    applyOverflowResult(secondPass.visibleCount, secondPass.hiddenCount);
  };

  const recalculateChipOverflow = async () => {
    if (isServer() || isMeasuring) {
      return;
    }

    isMeasuring = true;

    try {
      await measureOverflow();
    } finally {
      isMeasuring = false;
    }
  };

  const { connect, disconnect } = createDebouncedResizeObserver(
    () => servicesContainerRef.value,
    () => void recalculateChipOverflow(),
    () => isMeasuring,
  );

  const setupChipOverflow = async () => {
    await recalculateChipOverflow();
    await nextTick();
    connect();

    if (!isServer() && document.fonts) {
      void document.fonts.ready.then(() => recalculateChipOverflow());
    }
  };

  onMounted(setupChipOverflow);
  watch(hiddenChipLabelKeys, setupChipOverflow);
  onBeforeUnmount(disconnect);

  return {
    measureOverflowCount,
    visibleChipCount,
    hiddenChipCount,
    hiddenServicesTitle,
  };
};
